import { Group, Drawer, Burger, Menu, Accordion, Text } from '@mantine/core';
import { IconChevronDown, IconUserFilled } from '@tabler/icons-react';
import classes from './menu.module.css';
import { Link, useLocation } from 'react-router-dom';
import { rutas } from './rutas';
import { useLayoutEffect, useRef, useState } from 'react';
import { useAutenticacion } from '../../contextos/authContext';
import { usePermisos } from '../../contextos/usePermisos';
import { EnumPermisos } from '../../enums/enumPermisos';
import { listaReportesGenerales } from '../../../mocks/reportes/reportesGenerales';
import { listaReportesCliente } from '../../../mocks/reportes/reportesCliente';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SettingsIcon from '@mui/icons-material/Settings';

const MARGEN_IZQUIERDO_LINKS = 50;

export const MenuModulos = () => {
  const location = useLocation();
  const { estaAutenticado, usuario } = useAutenticacion();
  const { puedeAccederModulo } = usePermisos();
  const [abrirMenuLateral, setAbrirMenuLateral] = useState<boolean>(false);
  // El menú pasa a hamburguesa cuando los links no caben en una sola fila. Se mide en vez de usar un
  // breakpoint fijo porque la cantidad de módulos depende de los permisos de cada usuario.
  const [menuColapsado, setMenuColapsado] = useState<boolean>(false);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const contenedor = contenedorRef.current;
    const links = linksRef.current;
    if (!contenedor || !links) return;

    const evaluarEspacio = () => {
      // Los links siguen en el DOM (ocultos) cuando está colapsado, así su ancho real siempre se puede medir
      const anchoNecesario = links.scrollWidth + MARGEN_IZQUIERDO_LINKS;
      setMenuColapsado(anchoNecesario > contenedor.clientWidth);
    };

    const observador = new ResizeObserver(evaluarEspacio);
    observador.observe(contenedor);
    observador.observe(links);
    evaluarEspacio();
    return () => observador.disconnect();
  }, [estaAutenticado]);

  if (!estaAutenticado) {
    return null;
  }

  // Determinar qué lista de reportes y ruta base usar según permisos
  const esReportesGenerales = puedeAccederModulo(EnumPermisos.PERMISO_REPORTES_GENERALES_VER);
  const listaReportes = esReportesGenerales ? listaReportesGenerales : listaReportesCliente;
  const rutaBaseReportes = esReportesGenerales ? '/Reportes' : '/Cliente/Reportes';
  const puedeConfigurarNbs = puedeAccederModulo(EnumPermisos.PERMISO_COTIZADOR_NBS_CONFIGURAR);
  const puedeVerCotizacionesNbs = puedeAccederModulo(EnumPermisos.PERMISO_COTIZADOR_NBS_VER);
  const puedeConfigurarServicios = puedeAccederModulo(EnumPermisos.PERMISO_COTIZADOR_SERVICIOS_CONFIGURAR);
  const puedeVerCotizacionesServicios = puedeAccederModulo(EnumPermisos.PERMISO_COTIZADOR_SERVICIOS_VER);
  const puedeConfigurarPostventa = puedeAccederModulo(EnumPermisos.PERMISO_COTIZADOR_POSTVENTA_CONFIGURAR);
  const puedeVerCotizacionesPostventa = puedeAccederModulo(EnumPermisos.PERMISO_COTIZADOR_POSTVENTA_VER);
  const puedeConfigurarAlgunCotizador = puedeConfigurarNbs || puedeConfigurarServicios || puedeConfigurarPostventa;
  const puedeVerAlgunasCotizaciones = puedeVerCotizacionesNbs || puedeVerCotizacionesServicios || puedeVerCotizacionesPostventa;
  // El grupo Cotizadores se muestra una sola vez, anclado a la primera ruta de cotizadores permitida.
  const rutaAnclaCotizador = rutas.find(r => r.Activa && r.Ruta.startsWith('/Cotizadores') && puedeAccederModulo(r.PermisoRequerido))?.Ruta ?? null;

  // Filtrar por permisos individuales de cada reporte
  const listaReportesVisibles = listaReportes.filter(r => puedeAccederModulo(r.permisoRequerido));

  const esRutaReportesActiva = location.pathname === '/Reportes' || location.pathname === '/Cliente/Reportes';
  const queryParams = new URLSearchParams(location.search);
  const reporteActivo = queryParams.get('reporte') || listaReportesVisibles[0]?.slug || '';

  const items = rutas
    .filter((rutaFiltro) => rutaFiltro.Activa && !rutaFiltro.OcultoEnMenu && puedeAccederModulo(rutaFiltro.PermisoRequerido))
    .map((ruta) => {
      const esReportes =
        ruta.Ruta === '/Reportes' || ruta.Ruta === '/Cliente/Reportes';

      const esCotizador = ruta.Ruta.startsWith('/Cotizadores');

      // Evitar duplicado: si ya renderizamos el dropdown de reportes generales, omitir el de cliente
      const yaRenderizado =
        ruta.Ruta === '/Cliente/Reportes' && esReportesGenerales;
      if (yaRenderizado) return null;

      const yaRenderizadoCotizador = esCotizador && ruta.Ruta !== rutaAnclaCotizador;
      if (yaRenderizadoCotizador) return null;

      if (esReportes && listaReportesVisibles.length > 1) {
        return (
          <Menu
            key={ruta.NombreModulo}
            trigger="hover"
            openDelay={50}
            closeDelay={100}
            shadow="md"
            width={260}
            position="bottom-start"
            classNames={{ dropdown: classes.reportesDropdown }}
            zIndex={10000}
          >
            <Menu.Target>
              <span
                className={`${classes.link} ${esRutaReportesActiva ? classes.active : ''}`}
                style={{ cursor: 'pointer' }}
              >
                {ruta.Icono}
                {ruta.NombreModulo}
                <IconChevronDown size={14} />
              </span>
            </Menu.Target>
            <Menu.Dropdown>
              {listaReportesVisibles.map((reporte) => {
                const esActivo = reporteActivo === reporte.slug && esRutaReportesActiva;
                return (
                  <Menu.Item
                    key={reporte.slug}
                    component={Link}
                    to={`${rutaBaseReportes}?reporte=${reporte.slug}`}
                    onClick={() => setAbrirMenuLateral(false)}
                    className={esActivo ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                  >
                    <Group gap={10} wrap="nowrap" align="flex-start">
                      {reporte.icono && (
                        <span className={classes.reportesItemIcono}>{reporte.icono}</span>
                      )}
                      <div>
                        <div className={classes.reportesItemNombre}>{reporte.nombre}</div>
                        {reporte.descripcion && (
                          <div className={classes.reportesItemDesc}>{reporte.descripcion}</div>
                        )}
                      </div>
                    </Group>
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>
        );
      }

      // Solo un reporte visible: link directo sin dropdown
      if (esReportes && listaReportesVisibles.length === 1) {
        return (
          <Link
            key={ruta.NombreModulo}
            to={`${rutaBaseReportes}?reporte=${listaReportesVisibles[0].slug}`}
            className={`${classes.link} ${esRutaReportesActiva ? classes.active : ''}`}
            onClick={() => setAbrirMenuLateral(false)}
          >
            {ruta.Icono}
            {ruta.NombreModulo}
          </Link>
        );
      }

      if (esCotizador) {
        const esRutaCotizadorActiva = location.pathname.startsWith('/Cotizadores');
        return (
          <Menu
            key="CotizadorDropdown"
            trigger="hover"
            openDelay={50}
            closeDelay={100}
            shadow="md"
            width={260}
            position="bottom-start"
            classNames={{ dropdown: classes.reportesDropdown }}
            zIndex={10000}
          >
            <Menu.Target>
              <span
                className={`${classes.link} ${esRutaCotizadorActiva ? classes.active : ''}`}
                style={{ cursor: 'pointer' }}
              >
                <RequestQuoteIcon fontSize="small" />
                Cotizadores
                <IconChevronDown size={14} />
              </span>
            </Menu.Target>
            <Menu.Dropdown>
              {puedeConfigurarAlgunCotizador && (
                <Menu trigger="hover" position="right-start" offset={0} width={260} shadow="md" classNames={{ dropdown: classes.reportesDropdown }}>
                  <Menu.Target>
                    <Menu.Item rightSection={<IconChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} />}>
                      <Group gap={10} wrap="nowrap" align="flex-start">
                        <span className={classes.reportesItemIcono}><SettingsIcon fontSize="small" /></span>
                        <div>
                          <div className={classes.reportesItemNombre}>Configuración</div>
                          <div className={classes.reportesItemDesc}>Parámetros de cotizadores</div>
                        </div>
                      </Group>
                    </Menu.Item>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {puedeConfigurarNbs && (
                      <Menu.Item
                        component={Link}
                        to="/Cotizadores/Configuracion/NBS"
                        onClick={() => setAbrirMenuLateral(false)}
                        className={location.pathname === '/Cotizadores/Configuracion/NBS' ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                      >
                        <Group gap={10} wrap="nowrap" align="flex-start">
                          <span className={classes.reportesItemIcono}><SettingsIcon fontSize="small" /></span>
                          <div>
                            <div className={classes.reportesItemNombre}>NBS</div>
                            <div className={classes.reportesItemDesc}>Parámetros Comerciales NBS</div>
                          </div>
                        </Group>
                      </Menu.Item>
                    )}
                    {puedeConfigurarServicios && (
                      <Menu.Item
                        component={Link}
                        to="/Cotizadores/Configuracion/Servicios"
                        onClick={() => setAbrirMenuLateral(false)}
                        className={location.pathname === '/Cotizadores/Configuracion/Servicios' ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                      >
                        <Group gap={10} wrap="nowrap" align="flex-start">
                          <span className={classes.reportesItemIcono}><SettingsIcon fontSize="small" /></span>
                          <div>
                            <div className={classes.reportesItemNombre}>Servicios</div>
                            <div className={classes.reportesItemDesc}>Parámetros Comerciales Servicios</div>
                          </div>
                        </Group>
                      </Menu.Item>
                    )}
                    {puedeConfigurarPostventa && (
                      <Menu.Item
                        component={Link}
                        to="/Cotizadores/Configuracion/Postventa"
                        onClick={() => setAbrirMenuLateral(false)}
                        className={location.pathname === '/Cotizadores/Configuracion/Postventa' ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                      >
                        <Group gap={10} wrap="nowrap" align="flex-start">
                          <span className={classes.reportesItemIcono}><SettingsIcon fontSize="small" /></span>
                          <div>
                            <div className={classes.reportesItemNombre}>Postventa</div>
                            <div className={classes.reportesItemDesc}>Parámetros MOD QR y SEB</div>
                          </div>
                        </Group>
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}

              {puedeVerAlgunasCotizaciones && (
                <Menu trigger="hover" position="right-start" offset={0} width={260} shadow="md" classNames={{ dropdown: classes.reportesDropdown }}>
                  <Menu.Target>
                    <Menu.Item rightSection={<IconChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} />}>
                      <Group gap={10} wrap="nowrap" align="flex-start">
                        <span className={classes.reportesItemIcono}><RequestQuoteIcon fontSize="small" /></span>
                        <div>
                          <div className={classes.reportesItemNombre}>Cotizaciones</div>
                          <div className={classes.reportesItemDesc}>Gestión de cotizaciones</div>
                        </div>
                      </Group>
                    </Menu.Item>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {puedeVerCotizacionesNbs && (
                      <Menu.Item
                        component={Link}
                        to="/Cotizadores/Cotizaciones/NBS"
                        onClick={() => setAbrirMenuLateral(false)}
                        className={location.pathname === '/Cotizadores/Cotizaciones/NBS' ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                      >
                        <Group gap={10} wrap="nowrap" align="flex-start">
                          <span className={classes.reportesItemIcono}><RequestQuoteIcon fontSize="small" /></span>
                          <div>
                            <div className={classes.reportesItemNombre}>NBS</div>
                            <div className={classes.reportesItemDesc}>Gestión de cotizaciones NBS</div>
                          </div>
                        </Group>
                      </Menu.Item>
                    )}
                    {puedeVerCotizacionesServicios && (
                      <Menu.Item
                        component={Link}
                        to="/Cotizadores/Cotizaciones/Servicios"
                        onClick={() => setAbrirMenuLateral(false)}
                        className={location.pathname === '/Cotizadores/Cotizaciones/Servicios' ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                      >
                        <Group gap={10} wrap="nowrap" align="flex-start">
                          <span className={classes.reportesItemIcono}><RequestQuoteIcon fontSize="small" /></span>
                          <div>
                            <div className={classes.reportesItemNombre}>Servicios</div>
                            <div className={classes.reportesItemDesc}>Gestión de cotizaciones Servicios</div>
                          </div>
                        </Group>
                      </Menu.Item>
                    )}
                    {puedeVerCotizacionesPostventa && (
                      <Menu.Item
                        component={Link}
                        to="/Cotizadores/Cotizaciones/Postventa"
                        onClick={() => setAbrirMenuLateral(false)}
                        className={location.pathname === '/Cotizadores/Cotizaciones/Postventa' ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                      >
                        <Group gap={10} wrap="nowrap" align="flex-start">
                          <span className={classes.reportesItemIcono}><RequestQuoteIcon fontSize="small" /></span>
                          <div>
                            <div className={classes.reportesItemNombre}>Postventa</div>
                            <div className={classes.reportesItemDesc}>Modernizaciones y servicios especiales</div>
                          </div>
                        </Group>
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
            </Menu.Dropdown>
          </Menu>
        );
      }

      return (
        <Link
          key={ruta.NombreModulo}
          to={ruta.Ruta}
          className={`${classes.link} ${location.pathname === ruta.Ruta ? classes.active : ''}`}
          onClick={() => setAbrirMenuLateral(false)}
        >
          {ruta.Icono}
          {ruta.NombreModulo}
        </Link>
      );
    });

  // Renderizar navegación del Drawer (Mobile)
  const renderDrawerNav = () => {
    return rutas
      .filter((rutaFiltro) => rutaFiltro.Activa && puedeAccederModulo(rutaFiltro.PermisoRequerido))
      .map((ruta) => {
        const esReportes = ruta.Ruta === '/Reportes' || ruta.Ruta === '/Cliente/Reportes';
        const yaRenderizado = ruta.Ruta === '/Cliente/Reportes' && esReportesGenerales;
        if (yaRenderizado) return null;

        const esCotizador = ruta.Ruta.startsWith('/Cotizadores');

        const yaRenderizadoCotizador = esCotizador && ruta.Ruta !== rutaAnclaCotizador;
        if (yaRenderizadoCotizador) return null;

        if (esReportes && listaReportesVisibles.length > 1) {
          return (
            <Accordion key={ruta.NombreModulo} variant="default" className={classes.drawerAccordion}>
              <Accordion.Item value="reportes" style={{ border: 'none', backgroundColor: 'transparent' }}>
                <Accordion.Control
                  className={`${classes.drawerAccordionControl} ${esRutaReportesActiva ? classes.drawerLinkActive : ''}`}
                  icon={<span className={classes.drawerAccordionIcon}>{ruta.Icono}</span>}
                >
                  {ruta.NombreModulo}
                </Accordion.Control>
                <Accordion.Panel>
                  {listaReportesVisibles.map((reporte) => {
                    const esActivo = reporteActivo === reporte.slug && esRutaReportesActiva;
                    return (
                      <Link
                        key={reporte.slug}
                        to={`${rutaBaseReportes}?reporte=${reporte.slug}`}
                        className={`${classes.drawerLink} ${classes.drawerSubLink} ${esActivo ? classes.drawerSubLinkActive : ''}`}
                        onClick={() => setAbrirMenuLateral(false)}
                      >
                        <Group gap={10} wrap="nowrap">
                          {reporte.icono && (
                            <span className={classes.reportesItemIcono} style={{ display: 'flex', fontSize: '1.1rem' }}>
                              {reporte.icono}
                            </span>
                          )}
                          <Text size="sm">{reporte.nombre}</Text>
                        </Group>
                      </Link>
                    );
                  })}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          );
        }

        if (esCotizador) {
          const esRutaCotizadorActiva = location.pathname.startsWith('/Cotizadores');
          return (
            <Accordion key="CotizadorDrawer" variant="default" className={classes.drawerAccordion}>
              <Accordion.Item value="cotizador" style={{ border: 'none', backgroundColor: 'transparent' }}>
                <Accordion.Control
                  className={`${classes.drawerAccordionControl} ${esRutaCotizadorActiva ? classes.drawerLinkActive : ''}`}
                  icon={<span className={classes.drawerAccordionIcon}><RequestQuoteIcon fontSize="small" /></span>}
                >
                  Cotizadores
                </Accordion.Control>
                <Accordion.Panel>
                  <Accordion variant="default" className={classes.drawerAccordion}>
                    {puedeConfigurarAlgunCotizador && (
                      <Accordion.Item value="configuracion" style={{ border: 'none', backgroundColor: 'transparent' }}>
                        <Accordion.Control
                          className={classes.drawerAccordionControl}
                          icon={<span className={classes.drawerAccordionIcon}><SettingsIcon fontSize="small" /></span>}
                        >
                          Configuración
                        </Accordion.Control>
                        <Accordion.Panel>
                          {puedeConfigurarNbs && (
                            <Link
                              to="/Cotizadores/Configuracion/NBS"
                              className={`${classes.drawerLink} ${classes.drawerSubLink} ${location.pathname === '/Cotizadores/Configuracion/NBS' ? classes.drawerSubLinkActive : ''}`}
                              onClick={() => setAbrirMenuLateral(false)}
                            >
                              <Group gap={10} wrap="nowrap">
                                <Text size="sm">NBS</Text>
                              </Group>
                            </Link>
                          )}
                          {puedeConfigurarServicios && (
                            <Link
                              to="/Cotizadores/Configuracion/Servicios"
                              className={`${classes.drawerLink} ${classes.drawerSubLink} ${location.pathname === '/Cotizadores/Configuracion/Servicios' ? classes.drawerSubLinkActive : ''}`}
                              onClick={() => setAbrirMenuLateral(false)}
                            >
                              <Group gap={10} wrap="nowrap">
                                <Text size="sm">Servicios</Text>
                              </Group>
                            </Link>
                          )}
                          {puedeConfigurarPostventa && (
                            <Link
                              to="/Cotizadores/Configuracion/Postventa"
                              className={`${classes.drawerLink} ${classes.drawerSubLink} ${location.pathname === '/Cotizadores/Configuracion/Postventa' ? classes.drawerSubLinkActive : ''}`}
                              onClick={() => setAbrirMenuLateral(false)}
                            >
                              <Group gap={10} wrap="nowrap">
                                <Text size="sm">Postventa</Text>
                              </Group>
                            </Link>
                          )}
                        </Accordion.Panel>
                      </Accordion.Item>
                    )}

                    {puedeVerAlgunasCotizaciones && (
                      <Accordion.Item value="cotizaciones" style={{ border: 'none', backgroundColor: 'transparent' }}>
                        <Accordion.Control
                          className={classes.drawerAccordionControl}
                          icon={<span className={classes.drawerAccordionIcon}><RequestQuoteIcon fontSize="small" /></span>}
                        >
                          Cotizaciones
                        </Accordion.Control>
                        <Accordion.Panel>
                          {puedeVerCotizacionesNbs && (
                            <Link
                              to="/Cotizadores/Cotizaciones/NBS"
                              className={`${classes.drawerLink} ${classes.drawerSubLink} ${location.pathname === '/Cotizadores/Cotizaciones/NBS' ? classes.drawerSubLinkActive : ''}`}
                              onClick={() => setAbrirMenuLateral(false)}
                            >
                              <Group gap={10} wrap="nowrap">
                                <Text size="sm">NBS</Text>
                              </Group>
                            </Link>
                          )}
                          {puedeVerCotizacionesServicios && (
                            <Link
                              to="/Cotizadores/Cotizaciones/Servicios"
                              className={`${classes.drawerLink} ${classes.drawerSubLink} ${location.pathname === '/Cotizadores/Cotizaciones/Servicios' ? classes.drawerSubLinkActive : ''}`}
                              onClick={() => setAbrirMenuLateral(false)}
                            >
                              <Group gap={10} wrap="nowrap">
                                <Text size="sm">Servicios</Text>
                              </Group>
                            </Link>
                          )}
                          {puedeVerCotizacionesPostventa && (
                            <Link
                              to="/Cotizadores/Cotizaciones/Postventa"
                              className={`${classes.drawerLink} ${classes.drawerSubLink} ${location.pathname === '/Cotizadores/Cotizaciones/Postventa' ? classes.drawerSubLinkActive : ''}`}
                              onClick={() => setAbrirMenuLateral(false)}
                            >
                              <Group gap={10} wrap="nowrap">
                                <Text size="sm">Postventa</Text>
                              </Group>
                            </Link>
                          )}
                        </Accordion.Panel>
                      </Accordion.Item>
                    )}
                  </Accordion>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          );
        }

        const esActivo = location.pathname === ruta.Ruta;
        return (
          <Link
            key={ruta.NombreModulo}
            to={ruta.Ruta}
            className={`${classes.drawerLink} ${esActivo ? classes.drawerLinkActive : ''}`}
            onClick={() => setAbrirMenuLateral(false)}
          >
            <span className={classes.drawerLinkIcon}>{ruta.Icono}</span>
            <Text>{ruta.NombreModulo}</Text>
          </Link>
        );
      });
  };

  return (
    <header className={classes.headerMenuModulos}>
      <div className={`${classes.inner} ${classes.innerModulos}`} ref={contenedorRef}>
        {menuColapsado && (
          <Burger
            opened={abrirMenuLateral}
            onClick={() => setAbrirMenuLateral((v) => !v)}
            size="sm"
            className={classes.burger}
          />
        )}

        <Group
          ref={linksRef}
          ml={MARGEN_IZQUIERDO_LINKS}
          gap={5}
          wrap="nowrap"
          className={`${classes.links} ${menuColapsado ? classes.linksOcultos : ''}`}
          aria-hidden={menuColapsado}
          inert={menuColapsado}
        >
          {items}
        </Group>

        <Drawer
          opened={abrirMenuLateral}
          onClose={() => setAbrirMenuLateral(false)}
          padding="md"
          size="xs"
          title={<Text fw={700} size="lg">Menú</Text>}
          zIndex={10000}
        >
          <div className={classes.drawerWrapper}>
            <div>
              <div className={classes.drawerSectionTitle}>Módulos</div>
              <nav className={classes.drawerNav}>
                {renderDrawerNav()}
              </nav>
            </div>

            <div style={{ marginTop: 'auto' }}>
              <div style={{ padding: '20px 16px', borderTop: '1px solid var(--mantine-color-gray-2)', margin: '0 16px' }}>
                <Group gap="sm">
                  <IconUserFilled color="var(--mantine-color-primary-0)" size={24} />
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={700} style={{ color: 'var(--mantine-color-gray-8)' }}>{usuario?.NombreCompleto}</Text>
                    <Text size="xs" color="dimmed">Sesión activa</Text>
                  </div>
                </Group>
              </div>

              <div className={classes.logoDrawer}>
                <img
                  src="/logo-conekta-smart-mesa-trabajo-1.png"
                  width={120}
                  alt="Logo Footer"
                />
              </div>
            </div>
          </div>
        </Drawer>
      </div>
    </header>
  );
};